/* eslint-disable no-unneeded-ternary */
import { forwardRef, useImperativeHandle, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
// @mui
import {
    Box,
    Typography,
    Divider,
    Grid,
    MenuItem,
    ToggleButtonGroup,
    ToggleButton,
    Button,
    Stack
} from '@mui/material';
// components
import { Scrollbar } from '.';
import { FormProvider, RHFTextField, RHFSelect } from './hook-form';
// redux
import { bulkArchivedOrRestore, selectLeads, startArchivedLoading, bulkUpdate } from '../redux/slices/lead';
import { dispatch, useSelector } from '../redux/store';
import { pruneSelectedLeadIds } from '../utils/selectionModel';
// validation
import { bulkArchived } from '../validations/commonValidations';
// style
import { grey } from '../theme/palette';
import { addTreatmentGridStyle, toggleBtnStyle } from '../styles/AddTreatment';
import { archiveGridStyle, selectMenuItemStyle, selectStyle } from '../styles/DialogueTabs';
import { textFieldStyle } from '../styles/NewLead';
import { PATH_DASHBOARD } from '../routes/paths';
import { statusBadgeDoneStyle, statusBadgeRecommendedStyle } from '../styles/FailedToAttendTabs';

const ArchivedOrRestored = forwardRef(({ handleClose, practiceByLeadId }, ref) => {
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { multiLead } = useSelector((state) => state.lead);
    const [isCommunication, setCommunication] = useState('No');
    // The ids are captured here before selectLeads() clears them, so a
    // successful restore can still say WHICH lead it restored. Previously the
    // selection was wiped on submit, so by the time the request came back
    // nothing knew the lead any more and no "carry on" action was possible.
    const [submitting, setSubmitting] = useState(false);
    const [restoredLeadId, setRestoredLeadId] = useState(null);
    const navigate = useNavigate();
    const { reasons } = useSelector((state) => state.schema);

    const defaultValues = {
        notes: '',
        archiveReason: '',
        typed: multiLead?.type
    };

    const methods = useForm({
        resolver: yupResolver(bulkArchived),
        mode: 'onSubmit',
        defaultValues,
    });

    const {
        handleSubmit,
        getValues,
        setValue,
        watch,
    } = methods;

    const values = watch();

    useImperativeHandle(
        ref,
        () => ({
            async submit() {
                await handleSubmit(onSubmit)();
            },
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const onSubmit = async (data) => {
        // The dialog now stays open until the request resolves, so a second
        // click is possible where it was not before. Guard it.
        if (submitting) return;
        const payload = {
            object_ids: multiLead?.leadId,
            note: data.notes,
            request_type: multiLead?.type?.toUpperCase(),
        };
        const communicationPayload = {
            ids: multiLead?.leadId,
            value: getValues('isCommunication') === 'Yes' ? 'true' : 'false',
            field: 'is_open_for_communication'
        };
        // Deliberately NOT closing here. This used to call handleClose() before
        // the request was even sent, so a failed archive/restore had no dialog
        // left to report into and the user saw only a snackbar.
        const idsBeingActioned = Array.isArray(multiLead?.leadId) ? [...multiLead.leadId] : [];
        const isRestore = multiLead?.type !== 'Archive';
        setSubmitting(true);
        dispatch(selectLeads({ leadId: [], type: '', loading: true }));
        if (multiLead.type === 'Archive') {
            payload.reason = data.archiveReason;
            dispatch(bulkUpdate(practiceId, communicationPayload));
        }
        const selectionBefore = multiLead;
        dispatch(bulkArchivedOrRestore(practiceId, payload, (status, info) => {
            handleUpdate(status, { ids: idsBeingActioned, isRestore, selectionBefore, succeededIds: info?.succeededIds });
        }, practiceByLeadId));
    };

    const handleUpdate = (status, context = {}) => {
        setSubmitting(false);
        // The thunk now reports 'error' on a failed call. Keep the dialog open
        // so the error snackbar has something to be read next to, and so the
        // user can retry without re-selecting the leads.
        if (status?.toLowerCase?.() !== 'finish') {
            // onSubmit cleared the selection before the request. Put back ONLY the
            // ids that did not succeed so a retry re-sends just those (no duplicate
            // notes on leads that were already processed).
            if (context.selectionBefore && context.ids?.length) {
                const remaining = pruneSelectedLeadIds(context.ids, context.succeededIds || [], {
                    isFlatSelection: !!context.selectionBefore.isFlatSelection,
                });
                dispatch(selectLeads({ ...context.selectionBefore, leadId: remaining, loading: false }));
            }
            if (context.succeededIds?.length) dispatch(startArchivedLoading(true));
            return;
        }
        dispatch(startArchivedLoading(true));
        // Restored exactly one lead: offer to carry on with it rather than
        // closing and leaving the user to find it again in the table.
        if (context.isRestore && context.ids?.length === 1) {
            setRestoredLeadId(context.ids[0]);
            return;
        }
        handleClose(status);
    };

    const openRestoredLead = () => {
        const id = restoredLeadId;
        handleClose('finish');
        if (id) {
            navigate(
                PATH_DASHBOARD.general.leadDetail
                    .replace(':practiceId', practiceId)
                    .replace(':id', id)
                    .replace('/:tab?', '')
            );
        }
    };

    const handleChange = (e) => {
        setCommunication(e.target.value);
        setValue('isCommunication', e.target.value);
    };

    // Restored a single lead: show where it went and offer to open it. Closing
    // straight to a snackbar was the reported dead end — the lead was restored
    // but there was no way to carry on with it from here.
    if (restoredLeadId) {
        return (
            <Box sx={{ margin: '0 -8px' }} data-testid="restore-continue-panel">
                <Typography variant='subtitle2' sx={{ fontSize: '18px' }}>Lead restored</Typography>
                <Divider sx={{ borderStyle: 'dashed', mt: 1, mb: 2 }} />
                <Typography variant='body2' sx={{ color: grey[600], mb: 2 }}>
                    It is back in your active leads. You can open it now or carry on where you were.
                </Typography>
                <Stack direction='row' spacing={1}>
                    <Button variant='contained' onClick={openRestoredLead}>Open lead</Button>
                    <Button variant='outlined' color='inherit' onClick={() => handleClose('finish')}>Stay here</Button>
                </Stack>
            </Box>
        );
    }

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ margin: '0 -8px' }}>
                <Scrollbar sx={{ maxHeight: 64 * 10 }}>
                    <Typography variant='subtitle2' sx={{ fontSize: '18px' }}>Bulk {multiLead.type} {`(${multiLead?.leadId?.length} leads)`}</Typography>
                    <Divider sx={{ borderStyle: 'dashed', mt: 1 }} />
                    <Grid container spacing={1} sx={{ ...addTreatmentGridStyle, mt: 2 }}>
                        {multiLead.type === 'Archive' ?
                            <RHFSelect
                                name='archiveReason'
                                label='Select reason'
                                SelectProps={{ native: false, sx: selectStyle }}
                                sx={{ mt: 0 }}
                            >
                                {reasons?.map((options, index) => (
                                    <MenuItem
                                        key={index}
                                        value={options.value}
                                        sx={selectMenuItemStyle}
                                    >
                                        {options.label}
                                    </MenuItem>
                                ))}
                            </RHFSelect>
                            : <>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: 1, width: '100%' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Notes</Typography>
                                    {values.notes ? <Box component="span" sx={statusBadgeDoneStyle}>Done</Box> : <Box component="span" sx={statusBadgeRecommendedStyle}>Required</Box>}
                                </Box>
                                <RHFTextField name='notes' label='Restore notes' multiline rows={3} />
                            </>
                        }
                    </Grid>
                    {multiLead.type === 'Archive' && <Grid container spacing={1} sx={archiveGridStyle}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: 1, width: '100%' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Notes</Typography>
                            {values.notes ? <Box component="span" sx={statusBadgeDoneStyle}>Done</Box> : <Box component="span" sx={statusBadgeRecommendedStyle}>Required</Box>}
                        </Box>
                        <RHFTextField
                            sx={{ ...textFieldStyle, marginLeft: 0 }}
                            name='notes'
                            multiline
                            rows={4}
                            label='Archive notes'
                            placeholder='Add notes here'
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>}
                    {multiLead.type === 'Archive' && <>
                        <Grid container spacing={1} sx={archiveGridStyle}>
                            <Typography variant='subtitle2' >Is the lead open to be contacted in the future ?</Typography>
                        </Grid>
                        <Grid container spacing={1} sx={archiveGridStyle}>
                            <Box sx={{ border: `0px solid ${grey[500]}`, borderRadius: '10px' }}>
                                <ToggleButtonGroup
                                    color='primary'
                                    name='isCommunication'
                                    value={isCommunication}
                                    exclusive
                                    onChange={handleChange}
                                    aria-label='Platform'
                                >
                                    <ToggleButton sx={toggleBtnStyle} value={'Yes'}>Yes</ToggleButton>
                                    <ToggleButton sx={toggleBtnStyle} value={'No'}>No</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Grid>
                    </>}
                </Scrollbar>
            </Box>
        </FormProvider>
    );
});

ArchivedOrRestored.propTypes = {
    handleClose: PropTypes.func,
    // Optional lead id -> practice id map (All Practices mode) so bulk
    // archive/restore is sent once per owning practice.
    practiceByLeadId: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string]))
};

export default ArchivedOrRestored;
